import {useState, useCallback, useEffect, type MouseEvent, type PropsWithChildren} from 'react'
import {ChevronDownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'

import styles from './ForYouItem.module.css'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import type {LegacyForYouItemProps} from './for-you-item.types'

export function LegacyForYouItem({
  leadingVisual,
  emphasizedLeadingVisual = false,
  title,
  ariaLabel,
  itemName,
  actionSlot,
  revealActionOnHover = false,
  fullWidthActionSlotOnNarrow = false,
  description,
  children,
  defaultOpen = false,
  padding = 'default',
  variant = 'default',
  busy,
  footer,
}: PropsWithChildren<LegacyForYouItemProps>) {
  const isExpandable = !!children
  const [expanded, setExpanded] = useState(defaultOpen)
  const isExpanded = isExpandable && expanded

  useEffect(() => {
    if (!isExpandable) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clear user-controlled expansion after content is removed.
      setExpanded(false)
    }
  }, [isExpandable])

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    const next = !isExpanded
    sendPullRequestAnalyticsEvent('overview.for_you_item_toggle', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: itemName,
      is_open: next,
    })
    setExpanded(next)
  }, [isExpanded, sendPullRequestAnalyticsEvent, itemName])

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.target instanceof Element && event.target.closest('a, button')) return
      toggle()
    },
    [toggle],
  )

  const labelText = ariaLabel ?? (typeof title === 'string' ? title : '')
  const toggleAriaLabel = `${isExpanded ? 'Collapse' : 'Expand'} ${labelText}`.trim()

  return (
    <li className={clsx(styles.container, variant === 'embedded' && styles.embedded)} aria-busy={busy}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the chevron toggle button provides the keyboard-accessible affordance */}
      <div
        className={clsx(
          styles.headerRow,
          isExpanded ? styles.expanded : styles.collapsed,
          isExpandable && styles.expandable,
          revealActionOnHover && styles.revealActionOnHover,
          fullWidthActionSlotOnNarrow && styles.fullWidthActionSlotOnNarrow,
          fullWidthActionSlotOnNarrow === 'row' && styles.fullRowActionSlotOnNarrow,
          variant === 'embedded' && styles.embedded,
          emphasizedLeadingVisual && styles.emphasizedLeadingVisual,
        )}
        onClick={isExpandable ? handleRowClick : undefined}
      >
        <div className={styles.leadingVisual}>{leadingVisual}</div>
        <div className={styles.title}>{title}</div>
        {description && !isExpanded ? <div className={styles.description}>{description}</div> : null}
        <div className={styles.trailing}>
          {actionSlot}
          {isExpandable && (
            <IconButton
              icon={ChevronDownIcon}
              aria-label={toggleAriaLabel}
              description={isExpanded ? 'Collapse item' : 'Expand item'}
              aria-expanded={isExpanded}
              onClick={toggle}
              size="small"
              variant="invisible"
              className={clsx(styles.chevronButton, !isExpanded && styles.collapsed)}
            />
          )}
        </div>
      </div>
      {isExpanded && <div className={clsx(styles.body, padding === 'default' && styles.padded)}>{children}</div>}
      {footer}
    </li>
  )
}
